import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import {
  WeeklyMenuWithNames,
  ItemWithName,
  AlternativeWithName,
  BoxWithName,
  BoxAlternativeWithName
} from '../services/deliverer/types';
import { delivererClient } from '../services/deliverer/DelivererClient';
import moment from 'moment';
import { useActiveWeeklyMenus } from './useActiveWeeklyMenu';
import { getWeeklyMenuKeyForDate } from '../helpers/weeklyMenu';

interface State {
  readonly weeklyMenu: WeeklyMenuWithNames | null;
  readonly error: boolean;
  readonly loading: boolean;
}

const initialState: State = {
  weeklyMenu: null,
  error: false,
  loading: true
};

export const useWeeklyMenu = (startDate: string | undefined) => {
  const [state, setState] = useState(initialState);

  const { activeWeeklyMenus } = useActiveWeeklyMenus();

  const query = useQuery<WeeklyMenuWithNames, Error>(
    ['weeklyMenu', startDate, activeWeeklyMenus],
    async () => {
      if (!startDate) throw new Error('No startDate provided');

      // Use weekly menu key from the active weekly menu given the date
      const weeklyMenuKey = getWeeklyMenuKeyForDate(startDate, activeWeeklyMenus);

      if (!weeklyMenuKey) {
        throw new Error(`Geen actief weekmenu gevonden voor ${startDate}`);
      }

      const weeklyMenuResponse = await delivererClient.getWeeklyMenu(weeklyMenuKey);

      const now = moment();
      const requestedDeliveryMoment = moment(startDate);

      const weeklyMenuItemsWithNames: ItemWithName[] = await Promise.all(
        weeklyMenuResponse.items
          .filter((weeklyMenuItem) => {
            if (weeklyMenuItem.saleStartDate && moment(weeklyMenuItem.saleStartDate).isAfter(now)) {
              return false; // Exclude items not yet on sale
            }

            if (
              weeklyMenuItem.saleEndDate &&
              moment(weeklyMenuItem.saleEndDate).endOf('day').isSameOrBefore(now)
            ) {
              return false; // Exclude items no longer on sale
            }

            if (
              weeklyMenuItem.deliverableStartDate &&
              moment(weeklyMenuItem.deliverableStartDate)
                .startOf('day')
                .isAfter(requestedDeliveryMoment)
            ) {
              return false; // Exclude items not yet deliverable by the start date
            }

            if (
              weeklyMenuItem.deliverableEndDate &&
              moment(weeklyMenuItem.deliverableEndDate)
                .endOf('day')
                .isSameOrBefore(requestedDeliveryMoment)
            ) {
              return false; // Exclude items no longer deliverable by the start date
            }

            return true;
          })
          .map(async (weeklyMenuItem) => {
            const productInformation = await delivererClient.getProductInformation(
              weeklyMenuItem.mainVariantProductId
            );

            // Fetch names for alternatives
            const alternativesWithNames: AlternativeWithName[] = await Promise.all(
              weeklyMenuItem.alternatives.map(async (alternative) => {
                const altProductInfo = await delivererClient.getProductInformation(
                  alternative.productId
                );
                return {
                  ...alternative,
                  name: altProductInfo.name ?? 'Unknown Alternative'
                };
              })
            );

            return {
              ...weeklyMenuItem,
              name: productInformation.name ?? 'Unknown Product', // fallback if name is not available
              category: productInformation.category ?? '',
              alternatives: alternativesWithNames
            };
          })
      );

      // Process boxes similar to items
      const weeklyMenuBoxesWithNames: BoxWithName[] = await Promise.all(
        weeklyMenuResponse.boxes
          .filter((box) => moment(box.deliverableStartDate).isSameOrBefore(startDate))
          .map(async (box) => {
            const productInformation = await delivererClient.getProductInformation(
              box.mainVariantBoxProductId
            );

            // Fetch product info for all box alternatives to get mainVariantProductId and portions
            const alternativesWithProductInfo = await Promise.all(
              box.alternatives.map(async (alternative) => {
                const altProductInfo = await delivererClient.getProductInformation(
                  alternative.productId
                );
                return {
                  alternative,
                  productInfo: altProductInfo
                };
              })
            );

            // Group alternatives by mainVariantProductId
            const alternativesByMainVariant = new Map<
              string,
              {
                name: string;
                mealSequenceNumber: string;
                mainVariantProductId: string;
                portions: { portionSize: string; productId: string }[];
                position: number;
                alternativeText: string;
              }
            >();

            alternativesWithProductInfo.forEach(({ alternative, productInfo }) => {
              const mainVariantId = productInfo.mainVariantProductId ?? alternative.productId;

              if (!alternativesByMainVariant.has(mainVariantId)) {
                // Create new alternative group
                // Filter and map portions to ensure required fields are present
                const portions =
                  productInfo.portions
                    ?.filter((p) => p.portionSize && p.productId)
                    .map((p) => ({
                      portionSize: p.portionSize!,
                      productId: p.productId!
                    })) ?? [];

                alternativesByMainVariant.set(mainVariantId, {
                  name: productInfo.name ?? 'Unknown Alternative',
                  mealSequenceNumber: alternative.mealSequenceNumber,
                  mainVariantProductId: mainVariantId,
                  portions,
                  position: alternative.position,
                  alternativeText: alternative.alternativeText
                });
              }
            });

            // Convert grouped alternatives to BoxAlternativeWithName[]
            const boxAlternativesWithNames: BoxAlternativeWithName[] = Array.from(
              alternativesByMainVariant.values()
            ).map((group) => ({
              productId: group.mainVariantProductId,
              position: group.position,
              alternativeText: group.alternativeText,
              mealSequenceNumber: group.mealSequenceNumber,
              boxItems: [], // Not needed for form
              name: group.name,
              mainVariantProductId: group.mainVariantProductId,
              portions: group.portions.map((p) => ({
                portionSize: p.portionSize,
                productId: p.productId,
                boxItems: []
              }))
            }));

            return {
              ...box,
              name: productInformation.name ?? 'Unknown Box',
              category: productInformation.category ?? '',
              alternatives: boxAlternativesWithNames
            };
          })
      );

      return {
        ...weeklyMenuResponse,
        items: weeklyMenuItemsWithNames,
        boxes: weeklyMenuBoxesWithNames
      };
    },
    {
      enabled: !!startDate,
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      retry: false
    }
  );

  useEffect(() => {
    // Reset loading state when startDate changes
    setState((prev) => ({ ...prev, loading: true, error: false }));

    if (query.data) {
      setState({
        weeklyMenu: query.data,
        loading: false,
        error: false
      });
    } else if (query.isError) {
      setState({
        weeklyMenu: null,
        loading: false,
        error: true
      });
    }
  }, [query.data, query.isError, startDate]);

  return state;
};
